/**
 * Salary formatting utility for job listings
 * Ensures consistent display with £ sign, comma separators,
 * cadence suffixes (per year/month/week/day/hour), and "Up to" prefix.
 */

const CADENCE_SUFFIXES = {
  'per year': 'per year',
  'per annum': 'per year',
  'pa': 'per year',
  'p/a': 'per year',
  'per month': 'per month',
  'pcm': 'per month',
  'per week': 'per week',
  'pw': 'per week',
  'per day': 'per day',
  'pd': 'per day',
  'p/d': 'per day',
  'per hour': 'per hour',
  'ph': 'per hour',
  'p/h': 'per hour',
};

/**
 * Format a salary value for display
 * Handles: £ signs, comma separators, ranges, cadence suffixes, "Up to" prefix
 */
export const formatSalary = (salary) => {
  if (!salary && salary !== 0) return '';

  let salaryStr = String(salary).trim();
  if (!salaryStr) return '';

  // Normalize shorthand suffixes to full form
  salaryStr = salaryStr.replace(/\/hour/gi, ' per hour');
  salaryStr = salaryStr.replace(/\/year/gi, ' per year');
  salaryStr = salaryStr.replace(/\/month/gi, ' per month');
  salaryStr = salaryStr.replace(/\/week/gi, ' per week');
  salaryStr = salaryStr.replace(/\/day/gi, ' per day');

  // Detect and preserve "Up to" or "From" prefix
  let prefix = '';
  const upToMatch = salaryStr.match(/^up\s+to\s+/i);
  if (upToMatch) {
    prefix = 'Up to ';
    salaryStr = salaryStr.slice(upToMatch[0].length);
  }
  const fromMatch = !prefix && salaryStr.match(/^from\s+/i);
  if (fromMatch) {
    prefix = 'From ';
    salaryStr = salaryStr.slice(fromMatch[0].length);
  }

  // Detect and preserve cadence suffix
  let suffix = '';
  const lc = salaryStr.toLowerCase();
  for (const [pattern, normalised] of Object.entries(CADENCE_SUFFIXES)) {
    if (lc.endsWith(pattern)) {
      suffix = normalised;
      salaryStr = salaryStr.slice(0, -pattern.length).trim();
      break;
    }
  }

  // Check if it's a range (contains - or "to")
  const rangeMatch = salaryStr.match(/^(.+?)(?:\s*[-–—]\s*|\s+to\s+)(.+)$/i);

  if (rangeMatch) {
    const part1 = formatSingleSalary(rangeMatch[1]);
    const part2 = formatSingleSalary(rangeMatch[2]);
    return `${prefix}${part1} - ${part2}${suffix ? ' ' + suffix : ''}`;
  }

  return `${prefix}${formatSingleSalary(salaryStr)}${suffix ? ' ' + suffix : ''}`;
};

/**
 * Format a single salary value (not a range)
 */
const formatSingleSalary = (value) => {
  if (!value) return '';

  let str = String(value).trim();

  // Remove existing £ signs to avoid duplication
  str = str.replace(/£/g, '');

  // Check for remaining suffix (shouldn't be, but be safe)
  const suffixMatch = str.match(/^([\d,.\s]+)\s*(per\s+(?:hour|year|annum|month|week|day)|p\/h|ph|pa|pcm|pw|pd|k)?$/i);

  let numericPart = str;
  let trailingSuffix = '';

  if (suffixMatch) {
    numericPart = suffixMatch[1];
    trailingSuffix = suffixMatch[2] || '';
  }

  numericPart = numericPart.replace(/[,\s]/g, '');
  const num = parseFloat(numericPart);

  if (!isNaN(num)) {
    const formatted = num.toLocaleString('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return `£${formatted}${trailingSuffix ? ' ' + trailingSuffix : ''}`;
  }

  // Non-numeric labels like "Competitive"
  if (str.toLowerCase() === 'competitive' || str.toLowerCase() === 'negotiable') {
    return str;
  }

  if (/\d/.test(str)) return `£${str}`;
  return str;
};

export default formatSalary;
